import React, { useEffect, useState, useCallback } from 'react';
import TailwindLayout from '~/libs/ui/atoms/TailwindLayout/TailwindLayout';
import { SpinningLoadingIcon } from '~/libs/ui/atoms/SpinningLoadingIcon';
import {
	createErpScenarioIntervention,
	getErpPackages,
	getErpScenario,
	setErpScenarioInterventions,
	setErpVolumeGrowthCountryFigures,
	setErpVolumeGrowthPrices,
} from '~/libs/infrastructure/api/users';
import {
	ErpCountryWithPriceTypeResponse,
	ErpScenarioResponse,
	ErpSessionDetailResponse,
} from 'com.cogvio.pricemonitor.api';
import { useAccessToken } from '~/libs/infrastructure/api/authentication';
import { BaseScenario } from './src/components/BaseScenario';
import { ErpScenarioWithData, Interventions } from './src/components/Interventions';
import { ErpScenarioComparisonResults, pickScenario, ScenarioComparison } from './src/components/ScenarioComparison';
import { SortingContextProvider } from './src/store/sortingContext';
import NotFoundErrorPage from '~/UI/NotFoundErrorPage/NotFoundErrorPage';
import { PriceVolumeGrowthModal } from './src/components/PriceVolumeGrowthModal';
import { getErpCountries } from '~/libs/infrastructure/api/erp';
import { InterventionScenarioModal } from './src/components/InterventionScenarioModal';
import { BASE_SCENARIO_ID } from './src/components/ScenarioSelect';
import { ERPApiResult, ERPInterventionEditData, ERPPriceVolumeGrowth } from '~/libs/infrastructure/schemas/erp';
import { parseApiResult } from './src/utils/ApiParser';
import { VolumeGrowthPrice } from './src/components/PriceVolumeGrowthModalRow';
import { ScenarioHeader } from './src/components/ScenarioHeader';

interface IRPDetailPageProps {
	defaultCurrency: string | null;
	erpPackId: string;
	erpSessionId: string;
}

export function IRPDetailPage({ defaultCurrency, erpPackId, erpSessionId }: IRPDetailPageProps) {
	const accessToken = useAccessToken();
	const [error, setError] = useState(false);
	const [errorScenarioModal, setErrorScenarioModal] = useState<string>('');
	const [loading, setLoading] = useState(true);
	const [loadingScenario, setLoadingScenario] = useState(false);
	const [loadingComponent, setLoadingComponent] = useState(true);

	const [leftScenarioLoading, setLeftScenarioLoading] = useState(false);
	const [rightScenarioLoading, setRightScenarioLoading] = useState(false);

	const [scenarioModalLoading, setScenarioModalLoading] = useState(false);
	const [volumeGrowthModalLoading, setVolumeGrowthModalLoading] = useState(false);

	const [sessionDetail, setSessionDetail] = useState<ErpSessionDetailResponse>();
	const [erpSessionDate, setErpSessionDate] = useState<string>('');

	const [parseErpResults, setParseErpResults] = useState<ERPApiResult>();

	const [dataModalVisible, setDataModalVisible] = useState<boolean>(false);
	const [newScenarioModalVisible, setNewScenarioModalVisible] = useState<boolean>(false);
	const [scenarioIdToEdit, setScenarioIdToEdit] = useState<null | string>(null);

	const [scenariosResults, setScenariosResults] = useState<ErpScenarioWithData[]>([]);
	const [countriesResult, setCountriesResult] = useState<ErpCountryWithPriceTypeResponse[]>([]);

	const [leftSelectedScenarioId, setLeftSelectedScenarioId] = useState<string>(BASE_SCENARIO_ID);
	const [rightSelectedScenarioId, setRightSelectedScenarioId] = useState<string>('');
	const [leftScenarioResult, setLeftScenarioResult] = useState<ErpScenarioComparisonResults>();
	const [rightScenarioResult, setRightScenarioResult] = useState<ErpScenarioComparisonResults>();

	const toggleDataModalVisibility = useCallback(() => {
		setDataModalVisible((prev) => !prev);
	}, [setDataModalVisible]);

	const toggleNewScenarioModalVisibility = useCallback(() => {
		setNewScenarioModalVisible(true);
	}, [setNewScenarioModalVisible]);

	const hideInterventionScenarioModal = useCallback(() => {
		setScenarioIdToEdit(null);
		setNewScenarioModalVisible(false);
	}, [setNewScenarioModalVisible, setScenarioIdToEdit]);

	const getScenarioResult = (userId: string, list: ErpScenarioResponse[]) => {
		if (list.length <= 0) {
			return;
		}
		setLoadingScenario(true);
		setScenariosResults(list);

		list.forEach((scenario: ErpScenarioResponse, index: number, arr) => {
			getErpScenario(userId, erpPackId, erpSessionId, scenario.id)
				.then((data) => {
					setScenariosResults((prev) =>
						prev.map((s) =>
							s.id === scenario.id
								? { ...s, parseErpResults: parseApiResult(data.erpModelResult), data: data.erpModelResult }
								: s
						)
					);
					if (index === arr.length - 1) {
						setLoadingScenario(false);
					}
				})
				.catch(() => {
					setError(true);
					setLoadingScenario(false);
				});
		});
	};

	const getErpPackageSession = (userId: string) => {
		getErpPackages(userId, erpPackId, erpSessionId)
			.then((data) => {
				const parseApiData = parseApiResult(data.erpSessionDetail.erpModelResult);

				setSessionDetail(data.erpSessionDetail);
				setLeftScenarioResult({
					parseErpResults: parseApiData,
					data: data.erpSessionDetail.erpModelResult,
				});
				setRightScenarioResult({
					parseErpResults: parseApiData,
					data: data.erpSessionDetail.erpModelResult,
				});

				data.erpSessionDetail.erpSession.created && setErpSessionDate(data.erpSessionDetail.erpSession.created);
				getScenarioResult(userId, data.erpSessionDetail.erpSession.scenarios);

				setParseErpResults(parseApiData);
			})
			.catch(() => {
				setError(true);
			})
			.finally(() => {
				setLoading(false);
				setLoadingComponent(false);
			});

		getErpCountries()
			.then((data) => {
				setCountriesResult(data.items);
			})
			.catch(() => {
				setError(true);
			});
	};

	const savePriceVolumeGrowthModal = (priceData: VolumeGrowthPrice[], priceUserYoY: ERPPriceVolumeGrowth[]) => {
		if (accessToken === null) {
			return;
		}
		setVolumeGrowthModalLoading(true);

		setErpVolumeGrowthPrices(accessToken.user?.id, erpPackId, erpSessionId, priceData)
			.then(() => {
				setErpVolumeGrowthCountryFigures(accessToken.user?.id, erpPackId, erpSessionId, priceUserYoY)
					.then(() => {
						toggleDataModalVisibility();
						getErpPackageSession(accessToken.user?.id);

						setVolumeGrowthModalLoading(false);
					})
					.catch(() => {
						setError(true);
						setLoading(false);
					});
			})
			.catch(() => {
				setError(true);
				setVolumeGrowthModalLoading(false);
			});
	};

	const saveScenarioModal = (
		scenario: ErpScenarioResponse | null | undefined,
		scenarioEditData: ERPInterventionEditData[],
		nextScenarioName: string
	) => {
		if (accessToken === null) {
			return;
		}
		setScenarioModalLoading(true);

		if (scenario) {
			setErpScenarioInterventions(accessToken.user?.id, erpPackId, erpSessionId, scenario.id, scenarioEditData)
				.then(() => {
					hideInterventionScenarioModal();
					getErpPackageSession(accessToken.user?.id);

					setScenarioModalLoading(false);
				})
				.catch(() => {
					setScenarioModalLoading(false);
				});

			return;
		}

		createErpScenarioIntervention(accessToken.user?.id, erpPackId, erpSessionId, nextScenarioName)
			.then((data) => {
				setErpScenarioInterventions(accessToken.user?.id, erpPackId, erpSessionId, data.id, scenarioEditData)
					.then(() => {
						hideInterventionScenarioModal();
						getErpPackageSession(accessToken.user?.id);

						setScenarioModalLoading(false);
					})
					.catch(() => {
						setScenarioModalLoading(false);
					});
			})
			.catch((err) => {
				setScenarioModalLoading(false);
				setErrorScenarioModal(err.response.data.errors[0].code);
			});
	};

	const handleScenarioSelect = (scenarioId: string, type: string) => {
		if (accessToken === null) {
			return;
		}

		if (sessionDetail && scenarioId === BASE_SCENARIO_ID && type === 'left') {
			setLeftSelectedScenarioId(BASE_SCENARIO_ID);
			setLeftScenarioResult({
				parseErpResults: parseApiResult(sessionDetail.erpModelResult),
				data: sessionDetail.erpModelResult,
			});

			return;
		}
		if (sessionDetail && scenarioId === BASE_SCENARIO_ID && type === 'right') {
			setRightSelectedScenarioId(BASE_SCENARIO_ID);
			setRightScenarioResult({
				parseErpResults: parseApiResult(sessionDetail.erpModelResult),
				data: sessionDetail.erpModelResult,
			});

			return;
		}
		type === 'left' && setLeftScenarioLoading(true);
		type === 'right' && setRightScenarioLoading(true);

		getErpScenario(accessToken.user?.id, erpPackId, erpSessionId, scenarioId)
			.then((data) => {
				if (type === 'left') {
					setLeftSelectedScenarioId(scenarioId);
					setLeftScenarioResult({
						parseErpResults: parseApiResult(data.erpModelResult),
						data: data.erpModelResult,
					});
					setLeftScenarioLoading(false);
				}
				if (type === 'right') {
					setRightSelectedScenarioId(scenarioId);
					setRightScenarioResult({
						parseErpResults: parseApiResult(data.erpModelResult),
						data: data.erpModelResult,
					});
					setRightScenarioLoading(false);
				}
			})
			.catch(() => {
				setError(true);
				setLeftScenarioLoading(false);
				setRightScenarioLoading(false);
			});
	};

	const handleScenarioSwitch = () => {
		const currentLeftSideId = leftSelectedScenarioId;
		const currentLeftSideResult = leftScenarioResult;
		if (!rightSelectedScenarioId) {
			return;
		}

		setLeftSelectedScenarioId(rightSelectedScenarioId);
		setLeftScenarioResult(rightScenarioResult);

		setRightSelectedScenarioId(currentLeftSideId);
		setRightScenarioResult(currentLeftSideResult);
	};

	useEffect(() => {
		if (accessToken === null) {
			return;
		}

		getErpPackageSession(accessToken.user?.id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken]);

	return (
		<TailwindLayout>
			<div className='w-full h-full overflow-auto'>
				<ScenarioHeader
					erpPack={sessionDetail?.erpPack}
					erpSessionDate={erpSessionDate}
					loading={loadingComponent}
					error={error}
					onDataAddRequest={toggleDataModalVisibility}
				/>
				{loading && (
					<div className='w-full py-5 flex justify-center items-center'>
						<div className='h-12'>
							<SpinningLoadingIcon />
						</div>
					</div>
				)}
				{!loading && !error && sessionDetail && parseErpResults && accessToken && defaultCurrency && (
					<SortingContextProvider>
						<BaseScenario
							currency={defaultCurrency}
							erpModelResult={sessionDetail.erpModelResult}
							parseErpResults={parseErpResults}
							virtualProductPackName={sessionDetail?.erpPack?.virtualProductPackName || ''}
							onDataAddRequest={toggleDataModalVisibility}
						/>
						<Interventions
							currency={defaultCurrency}
							scenariosResults={scenariosResults}
							virtualProductPackName={sessionDetail?.erpPack?.virtualProductPackName || ''}
							loading={loadingScenario}
							onNewScenarioCreate={toggleNewScenarioModalVisibility}
							onScenarioEdit={setScenarioIdToEdit}
						/>
						{leftScenarioResult?.data && rightScenarioResult?.data && (
							<ScenarioComparison
								currency={defaultCurrency}
								parseErpResults={parseErpResults}
								erpSessionResult={sessionDetail}
								leftSelectedScenarioId={leftSelectedScenarioId}
								leftScenarioResult={leftScenarioResult}
								leftScenarioLoading={leftScenarioLoading}
								rightSelectedScenarioId={rightSelectedScenarioId}
								rightScenarioResult={rightScenarioResult}
								rightScenarioLoading={rightScenarioLoading}
								onScenarioSelect={handleScenarioSelect}
								onScenarioSwitch={handleScenarioSwitch}
								onDataAddRequest={toggleDataModalVisibility}
							/>
						)}
						{dataModalVisible && (
							<PriceVolumeGrowthModal
								currency={defaultCurrency}
								erpDate={erpSessionDate}
								erpSessionResult={sessionDetail}
								virtualProductPackName={sessionDetail?.erpPack || ''}
								erpCountriesResult={countriesResult}
								loading={volumeGrowthModalLoading}
								onClose={toggleDataModalVisibility}
								onSubmit={savePriceVolumeGrowthModal}
							/>
						)}
						{sessionDetail && Boolean(scenarioIdToEdit || newScenarioModalVisible) && (
							<InterventionScenarioModal
								currency={defaultCurrency}
								erpDate={erpSessionDate}
								erpSessionResult={sessionDetail}
								erpCountriesResult={countriesResult}
								scenario={
									(pickScenario(scenarioIdToEdit, sessionDetail.erpSession.scenarios) as ErpScenarioResponse) ||
									undefined
								}
								userId={accessToken.user?.id}
								loading={scenarioModalLoading}
								errorScenarioModal={errorScenarioModal}
								onClose={hideInterventionScenarioModal}
								onSubmit={saveScenarioModal}
							/>
						)}
					</SortingContextProvider>
				)}
				{!loading && !error && !sessionDetail && <NotFoundErrorPage />}
			</div>
		</TailwindLayout>
	);
}
